import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, BedSingle } from 'lucide-react';
import type { BedBoardCell } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { bedClassLabel } from './bed-board-look';

export function BedTransferDialog({
  cell,
  board,
  onClose,
}: {
  cell: BedBoardCell;
  board: readonly BedBoardCell[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [toBedId, setToBedId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const destinations = useMemo(() => {
    const free = board.filter((bed) => bed.status === 'vacant' && bed.bedId !== cell.bedId);
    const sameWardFirst = (bed: BedBoardCell) => (bed.wardId === cell.wardId ? 0 : 1);
    return [...free].sort((a, b) => sameWardFirst(a) - sameWardFirst(b));
  }, [board, cell.bedId, cell.wardId]);

  const transfer = useMutation({
    mutationFn: () => post(`/ipd/admissions/${cell.admissionId}/transfer`, { toBedId, reason: reason.trim() || undefined }),
    onSuccess: () => {
      toast.success(tr('ipd', 'patient_transferred'), tr('ipd', 'the_new_bed_class_is_charged_from_now'));
      void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
      void queryClient.invalidateQueries({ queryKey: ['admissions'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      icon={ArrowLeftRight}
      title={tr('ipd', 'transfer_bed')}
      description={tr('ipd', 'the_tariff_changes_from_the_moment_of_transfer')}
      dirty={toBedId !== '' || reason !== ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              transfer.mutate();
            }}
            loading={transfer.isPending}
            disabled={!toBedId}
          >
            {tr('ipd', 'transfer')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm">
          <span className="font-semibold">{cell.patientName}</span>
          <span className="text-muted-foreground"> · {tr('ipd', 'bed_x', { bedCode: cell.bedCode })}</span>
        </p>
        <Field label={tr('ipd', 'destination_bed')} required>
          {destinations.length === 0 ? (
            <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
              {tr('ipd', 'no_free_beds_to_move_to')}
            </p>
          ) : (
            <div
              role="radiogroup"
              aria-label={tr('ipd', 'select_a_free_bed')}
              className="grid max-h-72 grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2 overflow-y-auto p-0.5"
            >
              {destinations.map((bed) => {
                const chosen = bed.bedId === toBedId;
                return (
                  <label
                    key={bed.bedId}
                    className={cn(
                      'flex cursor-pointer items-start gap-2 rounded-lg border bg-card p-2 text-start transition-colors focus-within:ring-2 focus-within:ring-ring',
                      chosen ? 'border-success bg-success/10' : 'hover:border-success/50 hover:bg-success/5',
                    )}
                  >
                    <input
                      type="radio"
                      name="destination-bed"
                      value={bed.bedId}
                      checked={chosen}
                      onChange={() => setToBedId(bed.bedId)}
                      className="sr-only"
                    />
                    <BedSingle className={cn('mt-0.5 h-4 w-4 shrink-0', chosen ? 'text-success' : 'text-muted-foreground')} aria-hidden />
                    <span className="min-w-0">
                      <span className="block truncate font-mono text-sm font-semibold">{bed.bedCode}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">{bed.wardName}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">{bedClassLabel(bed.bedClass)}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </Field>
        <Field label={tr('ipd', 'reason')}>
          <Input maxLength={400} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
